import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, fetchClosePortfolio, fetchPlanningView } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] outline-none focus:border-[var(--gold)] mb-2";
const money = (v) => (v == null || v === "UNKNOWN" ? null : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const errMsg = (d, f) => (typeof d === "string" ? d : d?.message || d?.detail || f || "Something went wrong.");
const AREA_LABEL = { APPROVED_BUDGET_MISSING: "No approved budget", ACTUALS_UNKNOWN_NO_POSTED_JOURNALS: "No posted actuals", NO_FORECAST: "No forecast" };

const Unknown = ({ testid }) => <span className="italic text-[#e7a877]" data-testid={testid}>UNKNOWN</span>;
const Metric = ({ label, children, testid }) => (
  <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid={testid}>
    <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold)]/70">{label}</p>
    <div className="mt-1 text-lg text-[var(--bone)] font-serif2">{children}</div>
  </div>
);

function Login({ onIn }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false); const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr(null);
    try { await adminLogin(email, pw); onIn(); }
    catch (e) { setErr(errMsg(e.response?.data?.detail, "Login failed.")); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="planning-login-form">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl mb-4">Founder Planning</h1>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="planning-login-email" aria-label="Email" />
        <input className={inputCls} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="planning-login-password" aria-label="Password" />
        <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="planning-login-submit">{loading ? "…" : "Enter"}</button>
        {err && <p className="mt-3 text-sm text-[#e88f6a]" data-testid="planning-login-error">{err}</p>}
      </form>
    </div>
  );
}

function Console({ onSignOut }) {
  const [entities, setEntities] = useState([]);
  const [eid, setEid] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetchClosePortfolio().then((r) => {
      const list = (r.entities || []).map((e) => ({ id: e.entity_id, name: e.entity_name || e.legal_name || e.name || e.entity_id }));
      setEntities(list); if (!list.length) setErr("This workspace has no financial entities supported by verified source records. Establish those records before generating the calculations presented in this view."); if (list[0]) setEid(list[0].id);
    }).catch((e) => setErr(errMsg(e.response?.data?.detail, "Could not load entities.")));
  }, []);

  const load = useCallback(() => {
    if (!eid) return;
    setD(null); setErr(null);
    fetchPlanningView(eid, year).then(setD).catch((e) => setErr(errMsg(e.response?.data?.detail, "Planning view unavailable.")));
  }, [eid, year]);
  useEffect(() => { load(); }, [load]);

  const bva = d?.budget_vs_actual;
  const attention = [];
  if (d) {
    (d.assumptions_requiring_verification || []).forEach((a) => attention.push({ k: `asm-${a.assumption_id}`, t: `Assumption needs verification: “${a.statement}” (${a.evidence_class})` }));
    (d.unknown_or_incomplete_areas || []).forEach((c) => attention.push({ k: `area-${c}`, t: AREA_LABEL[c] || c }));
  }

  return (
    <div className="min-h-screen bg-[var(--navy)] text-[var(--bone)]">
      <header className="border-b border-[rgba(180,139,63,0.2)] px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-lg">Founder Planning Console</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs px-2 py-1" data-testid="planning-entity-select" aria-label="Entity" value={eid} onChange={(e) => setEid(e.target.value)}>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <input type="number" className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs px-2 py-1 w-24" data-testid="planning-year-input" aria-label="Fiscal year" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="planning-signout">Sign out</button>
        </div>
      </header>

      <main className="px-4 md:px-8 py-6 max-w-5xl">
        {err ? <p className="text-sm text-[#e88f6a]" data-testid="planning-error">{err}</p>
        : !d ? <p className="text-sm text-[var(--muted)]" data-testid="planning-loading">Loading planning view…</p>
        : (
          <div data-testid="planning-view">
            <p className="text-sm text-[var(--muted)] mb-1">{d.entity_name} · FY{d.fiscal_year} · <span data-testid="planning-consolidation">{d.consolidation_status}</span></p>
            <p className="text-[11px] text-[var(--muted)] mb-5">{d.authority_note}</p>

            <section className="mb-6" data-testid="attention-panel" aria-labelledby="attn-h">
              <h2 id="attn-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Needs Your Attention</h2>
              {attention.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid="attention-empty">Nothing flagged by the ledger for this period.</p>
              : <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]">
                  {attention.map((a) => <li key={a.k} className="px-4 py-2 text-sm text-[#e7a877]" data-testid={`attn-${a.k}`}>⚠ {a.t}</li>)}
                </ul>}
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Metric label="Approved budget" testid="metric-budget">{d.approved_budget ? `v${d.approved_budget.version} · ${d.approved_budget.state}` : <span className="text-[var(--muted)] text-sm" data-testid="state-no-budget">NO_APPROVED_BUDGET</span>}</Metric>
              <Metric label="Latest forecast" testid="metric-forecast">{d.latest_forecast ? <span data-testid="forecast-type">{d.latest_forecast.forecast_type}</span> : <span className="text-[var(--muted)] text-sm" data-testid="state-no-forecast">NO_FORECAST</span>}</Metric>
              <Metric label="Actual revenue" testid="metric-revenue">{d.actuals.status === "ACTUAL" ? (money(d.actuals.revenue) || "$0") : <Unknown testid="state-no-actuals" />}</Metric>
              <Metric label="Actual expense" testid="metric-expense">{d.actuals.status === "ACTUAL" ? (money(d.actuals.expense) || "$0") : <Unknown />}</Metric>
            </section>

            <section className="mb-6" aria-labelledby="bva-h">
              <h2 id="bva-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Budget vs Actual</h2>
              {bva?.status === "COMPUTED" ? (
                <div className="overflow-x-auto"><table className="w-full text-sm border border-[rgba(180,139,63,0.18)]" data-testid="bva-table">
                  <thead><tr className="text-left text-[var(--muted)] text-xs uppercase tracking-wide2">
                    <th className="px-3 py-2">Planned expense</th><th className="px-3 py-2">Actual expense</th><th className="px-3 py-2">Variance</th><th className="px-3 py-2">Remaining</th><th className="px-3 py-2">Classification</th></tr></thead>
                  <tbody><tr className="border-t border-[rgba(180,139,63,0.12)]">
                    <td className="px-3 py-2">{money(bva.planned_expense)}</td>
                    <td className="px-3 py-2">{money(bva.actual_expense)}</td>
                    <td className="px-3 py-2" data-testid="bva-variance">{money(bva.variance)}</td>
                    <td className="px-3 py-2">{money(bva.remaining_budget)}</td>
                    <td className="px-3 py-2" data-testid="bva-classification">{typeof bva.favorable === "boolean" ? (bva.favorable ? "Favorable" : "Unfavorable") : "—"}</td>
                  </tr></tbody>
                </table></div>
              ) : <p className="text-sm text-[var(--muted)] border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid="bva-unknown">Budget vs actual is <span className="italic text-[#e7a877]">UNKNOWN</span>{bva?.why ? ` — ${bva.why}` : ""}</p>}
            </section>

            <section className="mb-6" aria-labelledby="cash-h">
              <h2 id="cash-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Expected Cash</h2>
              <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid="expected-cash">
                <p className="text-sm text-[#e7a877] mb-2" data-testid="expected-cash-disclaimer">Expected cash is planning information, not actual cash.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div><p className="text-[var(--muted)] text-xs">Actual cash</p><Unknown testid="cash-actual" /></div>
                  <div><p className="text-[var(--muted)] text-xs">Expected inflows (invoices — not collected)</p><p data-testid="cash-inflow">{d.expected_cash.expected_inflow_invoice_count}</p></div>
                  <div><p className="text-[var(--muted)] text-xs">Expected outflows (bills — not paid)</p><p data-testid="cash-outflow">{d.expected_cash.expected_outflow_bill_count}</p></div>
                </div>
              </div>
            </section>

            <section aria-labelledby="scn-h">
              <h2 id="scn-h" className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Scenario Comparison</h2>
              {(d.scenario_comparison || []).length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid="scenario-empty">No scenarios yet.</p>
              : <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]" data-testid="scenario-comparison">
                  {d.scenario_comparison.map((s, i) => (
                    <li key={i} className="px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid={`scenario-${s.forecast_type}`}>
                      <span className="text-sm text-[var(--bone)]">{s.forecast_type} · v{s.version} · {s.state}</span>
                      <span className="text-[10px] uppercase tracking-wide2 text-[var(--muted)]">{s.labeled_as}</span>
                    </li>
                  ))}
                </ul>}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

export default function FounderPlanningConsole() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
